"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { RiAddLine, RiArchiveLine, RiSubtractLine } from "react-icons/ri";
import { LotSelectionDepletedToggle, LotSelectionSearch } from "@/components/inventory/lot-selection/lot-selection-controls";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { formatDate, formatQuantity, formatUnit } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import { Shipment } from "@/types/inventory/shipment";

// ============================================================================
// Types
// ============================================================================

export interface ShipmentAllocation {
  shipmentId: string;
  quantity: number;
  shipment: Shipment;
  allowExtraYield?: boolean;
  decisionKey?: string;
}
type PickerAllocation = {
  quantity: number;
  shipment: Shipment;
  allowExtraYield?: boolean;
  decisionKey?: string;
};

// Stock quantities are decimal (0.5 ml of a compounded cream), so sums carry
// binary-float noise: 0.1 + 0.2 is 0.30000000000000004. The server rounds
// quantities to six decimals, so match it here rather than comparing raw sums —
// otherwise an exact split reads as over-allocated and the save is held back.
export function roundQuantity(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
interface ShipmentPickerContextValue {
  listId: string;
  // State
  searchTerm: string;
  allocations: Record<string, PickerAllocation>;
  enableExtraYield: boolean;
  requireExplicitCoverage: boolean;
  exactCoverage: boolean;
  getCapacity: (shipment: Shipment) => number;
  canUseExtraYield: (shipment: Shipment) => boolean;
  showDepleted: boolean;
  setShowDepleted: (show: boolean) => void;

  // Props
  shipments: Shipment[];
  filteredShipments: Shipment[];
  excludeShipmentIds: string[];
  locationId?: string;
  unit: Unit;
  stockName: string;
  quantityNeeded?: number;

  // Actions
  setSearchTerm: (term: string) => void;
  toggleShipment: (shipment: Shipment) => void;
  selectShipment: (shipment: Shipment) => void;
  setQuantity: (shipmentId: string, quantity: number) => void;
  isSelected: (shipmentId: string) => boolean;
  getQuantity: (shipmentId: string) => number;

  // Computed
  coverageUnit: string;
  getShipmentUnit: (shipment: Shipment) => Unit;
  coverageDelta: number;
  totalAllocated: number;
  selectedCount: number;
}
const ShipmentPickerContext = createContext<ShipmentPickerContextValue | null>(null);
function useShipmentPickerContext() {
  const context = useContext(ShipmentPickerContext);
  if (!context) {
    throw new Error("ShipmentPicker components must be used within a ShipmentPicker.Root");
  }
  return context;
}

// ============================================================================
// Root Component (headless - no dialog)
// ============================================================================

interface ShipmentPickerRootProps {
  shipments: Shipment[];
  initialAllocations?: ShipmentAllocation[];
  locationId?: string;
  /** Shipment IDs to exclude from the list (e.g., already selected) */
  excludeShipmentIds?: string[];
  unit: Unit;
  stockName: string;
  /**
   * Total the caller has to cover. Newly picked lots default to whatever is
   * still unallocated instead of the lot's whole remaining quantity. Leave
   * unset when the caller has no target (a transfer takes what it takes).
   */
  quantityNeeded?: number;
  /** Physical units per target unit, for lots backing the same dose differently. */
  shipmentConversions?: Record<string, {
    factor: number;
    unit: Unit;
  }>;
  coverageUnit?: string;
  enableExtraYield?: boolean;
  /** Require the selected quantities to exactly cover quantityNeeded. */
  exactCoverage?: boolean;
  /** Usable capacity, including owned ledger credit; never physical display stock. */
  capacities?: Record<string, {
    capacity: number;
    allowExtraYield: boolean;
  }>;
  /** Caller scope/amount/unit/dilution snapshot for saved extra-yield allocations. */
  decisionKey?: string;
  onShowDepletedChange?: (show: boolean) => void;
  /** Initial discovery visibility; selected lots remain visible independently. */
  initialShowDepleted?: boolean;
  children: React.ReactNode;
}
function Root({
  shipments,
  initialAllocations = [],
  locationId,
  excludeShipmentIds = [],
  unit,
  stockName,
  quantityNeeded,
  shipmentConversions,
  coverageUnit = formatUnit(unit),
  enableExtraYield = false,
  exactCoverage = false,
  capacities,
  decisionKey,
  onShowDepletedChange,
  initialShowDepleted = false,
  children
}: ShipmentPickerRootProps) {
  const listId = useId();
  const factorFor = useCallback((shipment: Shipment) => shipmentConversions?.[shipment.id]?.factor ?? 1, [shipmentConversions]);
  const getShipmentUnit = (shipment_0: Shipment) => shipmentConversions?.[shipment_0.id]?.unit ?? unit;
  const [searchTerm, setSearchTerm] = useState("");
  const [showDepleted, updateShowDepleted] = useState(initialShowDepleted);
  const setShowDepleted = (show: boolean) => {
    updateShowDepleted(show);
    onShowDepletedChange?.(show);
  };
  const getCapacity = useCallback((shipment_1: Shipment) => {
    const capacity = capacities ? capacities[shipment_1.id]?.capacity ?? 0 : shipment_1.remainingQuantity;
    return Number.isFinite(capacity) ? Math.max(0, capacity) : 0;
  }, [capacities]);
  const canUseExtraYield = useCallback((shipment_2: Shipment) => enableExtraYield && (!locationId || shipment_2.locationId === locationId) && (!capacities || capacities[shipment_2.id]?.allowExtraYield === true), [enableExtraYield, locationId, capacities]);
  const [draftAllocations, setAllocations] = useState<Record<string, PickerAllocation>>(() => buildInitialAllocations(initialAllocations));
  // Saved receipts may arrive after depleted-lot discovery. Hydrate each once
  // without replacing edited quantities or restoring a deliberately removed lot.
  const hydratedReceiptIds = useRef(new Set(initialAllocations.map(allocation => allocation.shipmentId)));
  useEffect(() => {
    const arriving = initialAllocations.filter(allocation_0 => !hydratedReceiptIds.current.has(allocation_0.shipmentId));
    if (!arriving.length) return;
    arriving.forEach(allocation_1 => hydratedReceiptIds.current.add(allocation_1.shipmentId));
    setAllocations(previous => ({
      ...buildInitialAllocations(arriving),
      ...previous
    }));
  }, [initialAllocations]);

  // The chosen quantity determines extra yield. Derive both its warning and
  // backend flag from live eligibility/capacity so they cannot drift apart.
  const allocations = useMemo(() => Object.fromEntries(Object.entries(draftAllocations).map(([id, allocation_2]) => {
    const shipment_3 = shipments.find(candidate => candidate.id === id) ?? allocation_2.shipment;
    const extraYield = Number.isFinite(allocation_2.quantity) && canUseExtraYield(shipment_3) && roundQuantity(allocation_2.quantity - getCapacity(shipment_3)) > 0;
    return [id, {
      ...allocation_2,
      shipment: shipment_3,
      allowExtraYield: extraYield,
      decisionKey: extraYield && decisionKey !== undefined ? JSON.stringify([decisionKey, allocation_2.quantity]) : undefined
    }];
  })), [draftAllocations, shipments, canUseExtraYield, getCapacity, decisionKey]);
  const [requireExplicitCoverage, setRequireExplicitCoverage] = useState(() => enableExtraYield && initialAllocations.some(allocation_3 => {
    const otherAllocated = initialAllocations.reduce((sum, other) => other.shipmentId === allocation_3.shipmentId ? sum : sum + other.quantity / factorFor(other.shipment), 0);
    return allocation_3.allowExtraYield === true || quantityNeeded !== undefined && roundQuantity((quantityNeeded - otherAllocated) * factorFor(allocation_3.shipment)) > Math.max(0, allocation_3.shipment.remainingQuantity);
  }));

  // Filter shipments by location, exclusions, and search
  const filteredShipments = useMemo(() => {
    // A caller can stop supplying exhausted choices when visibility is hidden.
    // Keep exact selected receipts editable; fresh source data wins by identity.
    const candidates = new Map([...(enableExtraYield || capacities ? Object.values(allocations) : []).map(allocation_4 => [allocation_4.shipment.id, allocation_4.shipment] as const), ...shipments.map(shipment_4 => [shipment_4.id, shipment_4] as const)]);
    return Array.from(candidates.values()).filter(s => {
      if (locationId) {
        return (s.locationId === locationId || !!allocations[s.id] && getCapacity(s) > 0) && (s.remainingQuantity > 0 && getCapacity(s) > 0 || enableExtraYield && (showDepleted || !!allocations[s.id]));
      }
      return s.remainingQuantity > 0 && getCapacity(s) > 0 || enableExtraYield && (showDepleted || !!allocations[s.id]);
    }).filter(s_0 => !excludeShipmentIds.includes(s_0.id)).filter(s_1 => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (s_1.sku || "").toLowerCase().includes(searchLower) || (s_1.lotNumber || "").toLowerCase().includes(searchLower) || (s_1.supplier?.name || "").toLowerCase().includes(searchLower);
    }).sort((a, b) => {
      // Sort by expiry date first (FEFO), then by created date (FIFO)
      if (a.expiryDate && b.expiryDate) {
        return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
      }
      if (a.expiryDate) return -1;
      if (b.expiryDate) return 1;
      return new Date(a.createdDate || 0).getTime() - new Date(b.createdDate || 0).getTime();
    });
  }, [shipments, locationId, excludeShipmentIds, searchTerm, enableExtraYield, showDepleted, allocations, capacities, getCapacity]);

  // Keep coverage full precision: the editable values and capacity remain physical.
  const totalAllocated = useMemo(() => {
    const total = Object.values(allocations).reduce((sum_0, allocation_5) => sum_0 + allocation_5.quantity / factorFor(allocation_5.shipment), 0);
    return shipmentConversions ? total : roundQuantity(total);
  }, [allocations, factorFor, shipmentConversions]);
  const rawDelta = (quantityNeeded ?? totalAllocated) - totalAllocated;
  const coverageFactors = shipmentConversions ? Object.values(allocations).map(allocation_6 => factorFor(allocation_6.shipment)) : [1];
  const coverageDelta = coverageFactors.length > 0 && coverageFactors.every(factor => roundQuantity(rawDelta * factor) === 0) ? 0 : rawDelta;
  const selectedCount = Object.keys(allocations).length;

  // Toggle selection (for multi-select mode)
  const toggleShipment = useCallback((shipment_5: Shipment) => {
    if (!allocations[shipment_5.id] && getCapacity(shipment_5) <= 0 && !canUseExtraYield(shipment_5)) return;
    const outstanding = quantityNeeded === undefined ? 0 : roundQuantity((quantityNeeded - totalAllocated) * factorFor(shipment_5));
    if (enableExtraYield && !allocations[shipment_5.id] && getCapacity(shipment_5) < outstanding) {
      setRequireExplicitCoverage(true);
    }
    setAllocations(prev => {
      if (prev[shipment_5.id]) {
        const {
          [shipment_5.id]: _,
          ...rest
        } = prev;
        return rest;
      }
      const allocated = Object.values(prev).reduce((sum_1, alloc) => sum_1 + alloc.quantity / factorFor(alloc.shipment), 0);
      const outstanding_0 = quantityNeeded === undefined ? getCapacity(shipment_5) : Math.max(roundQuantity((quantityNeeded - allocated) * factorFor(shipment_5)), 0);
      return {
        ...prev,
        [shipment_5.id]: {
          quantity: roundQuantity(getCapacity(shipment_5) <= 0 && canUseExtraYield(shipment_5) ? outstanding_0 : Math.min(getCapacity(shipment_5), outstanding_0)),
          shipment: shipment_5
        }
      };
    });
  }, [quantityNeeded, factorFor, enableExtraYield, allocations, totalAllocated, getCapacity, canUseExtraYield]);

  // Select a single shipment (for single-select mode - replaces selection)
  const selectShipment = useCallback((shipment_6: Shipment) => {
    setAllocations({
      [shipment_6.id]: {
        quantity: getCapacity(shipment_6),
        shipment: shipment_6
      }
    });
  }, [getCapacity]);
  const setQuantity = useCallback((shipmentId: string, quantity: number) => {
    if (!Number.isFinite(quantity)) return;
    if (enableExtraYield) setRequireExplicitCoverage(true);
    setAllocations(prev_0 => {
      const alloc_0 = prev_0[shipmentId];
      if (!alloc_0 || !Number.isFinite(quantity)) return prev_0;
      const currentShipment = shipments.find(shipment_7 => shipment_7.id === shipmentId) ?? alloc_0.shipment;
      const clampedQuantity = Math.max(0, canUseExtraYield(currentShipment) ? quantity : Math.min(quantity, getCapacity(currentShipment)));
      if (clampedQuantity === 0 && !enableExtraYield) {
        const {
          [shipmentId]: _,
          ...rest_0
        } = prev_0;
        return rest_0;
      }
      return {
        ...prev_0,
        [shipmentId]: {
          ...alloc_0,
          shipment: currentShipment,
          quantity: clampedQuantity
        }
      };
    });
  }, [enableExtraYield, shipments, canUseExtraYield, getCapacity]);
  const isSelected = useCallback((shipmentId_0: string) => !!allocations[shipmentId_0], [allocations]);
  const getQuantity = useCallback((shipmentId_1: string) => allocations[shipmentId_1]?.quantity ?? 0, [allocations]);
  const contextValue: ShipmentPickerContextValue = {
    listId,
    searchTerm,
    allocations,
    enableExtraYield,
    requireExplicitCoverage,
    exactCoverage,
    getCapacity,
    canUseExtraYield,
    showDepleted,
    setShowDepleted,
    shipments,
    filteredShipments,
    excludeShipmentIds,
    locationId,
    unit,
    stockName,
    quantityNeeded,
    setSearchTerm,
    toggleShipment,
    selectShipment,
    setQuantity,
    isSelected,
    getQuantity,
    coverageUnit,
    getShipmentUnit,
    coverageDelta,
    totalAllocated,
    selectedCount
  };
  return <ShipmentPickerContext.Provider value={contextValue}>
      {children}
    </ShipmentPickerContext.Provider>;
}

// ============================================================================
// Dialog Wrapper (for multi-select with save/cancel)
// ============================================================================

interface DialogWrapperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (allocations: ShipmentAllocation[]) => void;
  title?: string;
  /** Reference-only callers can save documentation without selecting a physical draw. */
  allowEmptySelection?: boolean;
  /** Custom lot documentation can account for the untracked remainder, never an overdraw. */
  allowIncompleteCoverage?: boolean;
  emptySelectionLabel?: string;
  saveLabel?: string;
  saveDisabled?: boolean;
  onEscapeKeyDown?: React.ComponentProps<typeof DialogContent>["onEscapeKeyDown"];
  children: React.ReactNode;
}
function DialogWrapper(t0) {
  const $ = _c(68);
  const {
    open,
    onOpenChange,
    onSave,
    title: t1,
    allowEmptySelection: t2,
    allowIncompleteCoverage: t3,
    emptySelectionLabel: t4,
    saveLabel,
    saveDisabled: t5,
    onEscapeKeyDown,
    children
  } = t0;
  const title = t1 === undefined ? "Select lots" : t1;
  const allowEmptySelection = t2 === undefined ? false : t2;
  const allowIncompleteCoverage = t3 === undefined ? false : t3;
  const emptySelectionLabel = t4 === undefined ? "Clear Selection" : t4;
  const saveDisabled = t5 === undefined ? false : t5;
  const {
    allocations,
    stockName,
    coverageUnit,
    coverageDelta,
    totalAllocated,
    quantityNeeded,
    shipments,
    requireExplicitCoverage,
    exactCoverage,
    getCapacity,
    canUseExtraYield
  } = useShipmentPickerContext();
  let t6;
  if ($[0] !== allocations || $[1] !== canUseExtraYield || $[2] !== getCapacity || $[3] !== shipments) {
    t6 = Object.values(allocations).some(allocation => {
      const shipment = shipments.find(candidate => candidate.id === allocation.shipment.id) ?? allocation.shipment;
      return !Number.isFinite(allocation.quantity) || roundQuantity(allocation.quantity - getCapacity(shipment)) > 0 && (!canUseExtraYield(shipment) || !allocation.allowExtraYield);
    });
    $[0] = allocations;
    $[1] = canUseExtraYield;
    $[2] = getCapacity;
    $[3] = shipments;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const hasUnavailableQuantity = t6;
  const uncovered = quantityNeeded === undefined ? 0 : Math.max(0, coverageDelta);
  const hasEmptyLot = Object.values(allocations).some(_temp);
  let t7;
  if ($[5] !== allocations || $[6] !== allowEmptySelection || $[7] !== allowIncompleteCoverage || $[8] !== exactCoverage || $[9] !== requireExplicitCoverage || $[10] !== uncovered) {
    t7 = !allowIncompleteCoverage && (requireExplicitCoverage || exactCoverage || Object.values(allocations).some(_temp2)) && uncovered > 0 && !(allowEmptySelection && Object.keys(allocations).length === 0);
    $[5] = allocations;
    $[6] = allowEmptySelection;
    $[7] = allowIncompleteCoverage;
    $[8] = exactCoverage;
    $[9] = requireExplicitCoverage;
    $[10] = uncovered;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const incompleteChoice = t7;
  let t8;
  if ($[12] !== coverageDelta || $[13] !== coverageUnit || $[14] !== quantityNeeded) {
    t8 = quantityNeeded !== undefined && coverageDelta < 0 ? `That is more than the ${formatQuantity(quantityNeeded)} ${coverageUnit.toLocaleLowerCase()} needed — lower a lot to continue.` : null;
    $[12] = coverageDelta;
    $[13] = coverageUnit;
    $[14] = quantityNeeded;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const overAllocationMessage = t8;
  let t9;
  if ($[16] !== allocations || $[17] !== hasEmptyLot || $[18] !== hasUnavailableQuantity || $[19] !== incompleteChoice || $[20] !== onOpenChange || $[21] !== onSave || $[22] !== overAllocationMessage || $[23] !== saveDisabled) {
    t9 = () => {
      const result = Object.entries(allocations).filter(_temp3).map(_temp4);
      if (hasUnavailableQuantity || incompleteChoice || overAllocationMessage || hasEmptyLot || saveDisabled) {
        return;
      }
      onSave(result);
      onOpenChange(false);
    };
    $[16] = allocations;
    $[17] = hasEmptyLot;
    $[18] = hasUnavailableQuantity;
    $[19] = incompleteChoice;
    $[20] = onOpenChange;
    $[21] = onSave;
    $[22] = overAllocationMessage;
    $[23] = saveDisabled;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleSave = t9;
  let t10;
  if ($[25] !== title) {
    t10 = <DialogTitle>{title}</DialogTitle>;
    $[25] = title;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== stockName) {
    t11 = <p className="text-body text-foreground-muted">{stockName}</p>;
    $[27] = stockName;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <DialogHeader>{t10}{t11}</DialogHeader>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== children) {
    t13 = <DialogBody className="space-y-4">{children}</DialogBody>;
    $[32] = children;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== coverageUnit || $[35] !== hasEmptyLot || $[36] !== hasUnavailableQuantity || $[37] !== incompleteChoice || $[38] !== overAllocationMessage || $[39] !== uncovered) {
    t14 = (incompleteChoice || hasUnavailableQuantity || overAllocationMessage || hasEmptyLot) && <div className="flex w-full flex-col gap-2">{hasEmptyLot && <span role="status" className="text-caption text-foreground-secondary">Assign an amount to each selected lot, or deselect it. To split the total, reduce another lot first.</span>}{incompleteChoice && <span role="status" className="text-caption text-status-warning-text">Choose lots for the remaining {formatQuantity(uncovered)}{" "}{coverageUnit}.</span>}{hasUnavailableQuantity && <span role="alert" className="text-caption text-status-warning-text">This quantity is no longer available from the selected lot. Reduce it or choose another lot.</span>}{overAllocationMessage && <span className="text-caption text-status-warning-text">{overAllocationMessage}</span>}</div>;
    $[34] = coverageUnit;
    $[35] = hasEmptyLot;
    $[36] = hasUnavailableQuantity;
    $[37] = incompleteChoice;
    $[38] = overAllocationMessage;
    $[39] = uncovered;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== onOpenChange) {
    t15 = <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>;
    $[41] = onOpenChange;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const t16 = hasUnavailableQuantity || incompleteChoice || overAllocationMessage !== null || hasEmptyLot || saveDisabled;
  let t17;
  if ($[43] !== coverageUnit || $[44] !== emptySelectionLabel || $[45] !== incompleteChoice || $[46] !== saveLabel || $[47] !== totalAllocated) {
    t17 = saveLabel ?? (totalAllocated > 0 ? `Use ${formatQuantity(totalAllocated)} ${coverageUnit}` : incompleteChoice ? "Choose lots" : emptySelectionLabel);
    $[43] = coverageUnit;
    $[44] = emptySelectionLabel;
    $[45] = incompleteChoice;
    $[46] = saveLabel;
    $[47] = totalAllocated;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== handleSave || $[50] !== t16 || $[51] !== t17) {
    t18 = <Button type="button" variant="primary" onClick={handleSave} disabled={t16}>{t17}</Button>;
    $[49] = handleSave;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t15 || $[54] !== t18) {
    t19 = <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">{t15}{t18}</div>;
    $[53] = t15;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t14 || $[57] !== t19) {
    t20 = <DialogFooter className="flex-col items-stretch gap-3 space-x-0">{t14}{t19}</DialogFooter>;
    $[56] = t14;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== onEscapeKeyDown || $[60] !== t12 || $[61] !== t13 || $[62] !== t20) {
    t21 = <DialogContent className="w-full max-w-lg" onEscapeKeyDown={onEscapeKeyDown}>{t12}{t13}{t20}</DialogContent>;
    $[59] = onEscapeKeyDown;
    $[60] = t12;
    $[61] = t13;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== onOpenChange || $[65] !== open || $[66] !== t21) {
    t22 = <Dialog open={open} onOpenChange={onOpenChange}>{t21}</Dialog>;
    $[64] = onOpenChange;
    $[65] = open;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  return t22;
}

// ============================================================================
// Search Component
// ============================================================================
function _temp4(t0) {
  const [shipmentId, alloc_0] = t0;
  return {
    shipmentId,
    quantity: alloc_0.quantity,
    shipment: alloc_0.shipment,
    ...(alloc_0.allowExtraYield && {
      allowExtraYield: true,
      decisionKey: alloc_0.decisionKey
    })
  };
}
function _temp3(t0) {
  const [, alloc] = t0;
  return alloc.quantity > 0;
}
function _temp2(allocation_1) {
  return allocation_1.allowExtraYield;
}
function _temp(allocation_0) {
  return allocation_0.quantity <= 0;
}
interface SearchProps {
  placeholder?: string;
}
function Search(t0) {
  const $ = _c(5);
  const {
    placeholder: t1
  } = t0;
  const placeholder = t1 === undefined ? "Search by supplier, SKU, or lot..." : t1;
  const {
    searchTerm,
    setSearchTerm,
    listId
  } = useShipmentPickerContext();
  let t2;
  if ($[0] !== listId || $[1] !== placeholder || $[2] !== searchTerm || $[3] !== setSearchTerm) {
    t2 = <LotSelectionSearch value={searchTerm} onChange={setSearchTerm} listId={listId} placeholder={placeholder} />;
    $[0] = listId;
    $[1] = placeholder;
    $[2] = searchTerm;
    $[3] = setSearchTerm;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ============================================================================
// List Component
// ============================================================================

interface ListProps {
  /** Render prop for custom item rendering */
  children?: (shipments: Shipment[]) => React.ReactNode;
  emptyMessage?: string;
  className?: string;
}
function List(t0) {
  const $ = _c(13);
  const {
    children,
    emptyMessage: t1,
    className
  } = t0;
  const emptyMessage = t1 === undefined ? "No lots available" : t1;
  const {
    filteredShipments,
    searchTerm,
    enableExtraYield,
    listId
  } = useShipmentPickerContext();
  if (filteredShipments.length === 0) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <RiArchiveLine className="mb-2 h-8 w-8 text-foreground-muted" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    const t3 = searchTerm ? "No lots found" : emptyMessage;
    let t4;
    if ($[1] !== t3) {
      t4 = <div className="flex flex-col items-center justify-center py-10 text-center">{t2}<p className="text-body text-foreground-muted">{t3}</p></div>;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    return t4;
  }
  let t2;
  if ($[3] !== children) {
    t2 = rows => children ? children(rows) : rows.map(_temp5);
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const renderRows = t2;
  let t3;
  if ($[5] !== className || $[6] !== enableExtraYield || $[7] !== filteredShipments || $[8] !== listId || $[9] !== renderRows) {
    const available = filteredShipments.filter(_temp6);
    const depleted = filteredShipments.filter(_temp7);
    let t4;
    if ($[11] !== className) {
      t4 = cx("max-h-[min(480px,calc(90dvh_-_350px))] overflow-y-auto", className);
      $[11] = className;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = <div id={listId} className={t4} onKeyDown={_temp8}>{enableExtraYield ? <>{available.length > 0 && <section aria-label="Available lots" className="space-y-2"><h3 className="text-caption-strong text-foreground">Available lots</h3>{renderRows(available)}</section>}{depleted.length > 0 && <section aria-label="Depleted lots" className="space-y-2"><h3 aria-label="Depleted lots" className="text-caption-strong text-foreground">Depleted lots — extra yield required</h3>{renderRows(depleted)}</section>}</> : renderRows(filteredShipments)}</div>;
    $[5] = className;
    $[6] = enableExtraYield;
    $[7] = filteredShipments;
    $[8] = listId;
    $[9] = renderRows;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

// ============================================================================
// Item Component (with checkbox for multi-select)
// ============================================================================
function _temp8(event) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
    return;
  }
  const choices = Array.from(event.currentTarget.querySelectorAll("[data-lot-picker-choice]:not(:disabled)"));
  const index = choices.indexOf(event.target as HTMLButtonElement);
  if (index < 0 || !choices.length) {
    return;
  }
  event.preventDefault();
  choices[(index + (event.key === "ArrowDown" ? 1 : -1) + choices.length) % choices.length].focus();
}
function _temp7(shipment_1) {
  return shipment_1.remainingQuantity <= 0;
}
function _temp6(shipment_0) {
  return shipment_0.remainingQuantity > 0;
}
function _temp5(shipment) {
  return <Item key={shipment.id} shipment={shipment} />;
}
interface ItemProps {
  shipment: Shipment;
  details?: React.ReactNode;
  children?: React.ReactNode;
}
function Item(t0) {
  const $ = _c(37);
  const {
    shipment,
    details,
    children
  } = t0;
  const {
    isSelected,
    toggleShipment,
    getCapacity,
    canUseExtraYield
  } = useShipmentPickerContext();
  let t1;
  if ($[0] !== isSelected || $[1] !== shipment.id) {
    t1 = isSelected(shipment.id);
    $[0] = isSelected;
    $[1] = shipment.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  const selectionId = useId();
  const revealControls = _temp9;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("border-b border-border py-2 transition-colors last:border-b-0");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canUseExtraYield || $[5] !== getCapacity || $[6] !== selected || $[7] !== shipment) {
    t3 = !selected && getCapacity(shipment) <= 0 && !canUseExtraYield(shipment);
    $[4] = canUseExtraYield;
    $[5] = getCapacity;
    $[6] = selected;
    $[7] = shipment;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== shipment || $[10] !== toggleShipment) {
    t4 = event => {
      if (event.key === "Enter") {
        event.preventDefault();
        toggleShipment(shipment);
      }
    };
    $[9] = shipment;
    $[10] = toggleShipment;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = `Select lot ${shipment.lotNumber || shipment.sku || "unlabeled"} (${shipment.id})`;
  let t6;
  if ($[12] !== shipment || $[13] !== toggleShipment) {
    t6 = () => toggleShipment(shipment);
    $[12] = shipment;
    $[13] = toggleShipment;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== selected || $[16] !== selectionId || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <Checkbox id={selectionId} checked={selected} disabled={t3} data-lot-picker-choice={true} onKeyDown={t4} aria-label={t5} className="mt-0.5 border-border bg-background" onCheckedChange={t6} />;
    $[15] = selected;
    $[16] = selectionId;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== details || $[23] !== shipment) {
    t8 = details ?? <ItemDetails shipment={shipment} />;
    $[22] = details;
    $[23] = shipment;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== selectionId || $[26] !== t8) {
    t9 = <label htmlFor={selectionId} className="min-w-0 flex-1 cursor-pointer">{t8}</label>;
    $[25] = selectionId;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t7 || $[29] !== t9) {
    t10 = <div className="flex items-start gap-3 px-2 py-1">{t7}{t9}</div>;
    $[28] = t7;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== children || $[32] !== selected) {
    t11 = selected && <div ref={revealControls}>{children}</div>;
    $[31] = children;
    $[32] = selected;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <div className={t2}>{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}

// ============================================================================
// Simple Item Component (click to select, no checkbox - for single select)
// ============================================================================
function _temp9(node) {
  node?.scrollIntoView?.({
    block: "nearest"
  });
}
interface SimpleItemProps {
  shipment: Shipment;
  onClick?: (shipment: Shipment) => void;
}
function SimpleItem(t0) {
  const $ = _c(8);
  const {
    shipment,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick || $[1] !== shipment) {
    t1 = () => onClick?.(shipment);
    $[0] = onClick;
    $[1] = shipment;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== shipment) {
    t2 = <ItemDetails shipment={shipment} />;
    $[3] = shipment;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <button type="button" className="flex w-full flex-col rounded-lg border border-border p-3 text-left transition-colors md:hover:bg-background-secondary/50" onClick={t1}>{t2}</button>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

// ============================================================================
// Item Details (shared between Item and SimpleItem)
// ============================================================================

interface ItemDetailsProps {
  shipment: Shipment;
}
function ItemDetails(t0) {
  const $ = _c(35);
  const {
    shipment
  } = t0;
  const {
    getShipmentUnit
  } = useShipmentPickerContext();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== getShipmentUnit || $[1] !== shipment) {
    const unit = getShipmentUnit(shipment);
    t5 = "min-w-0 flex-1";
    const t8 = shipment.supplier?.name ?? "Unknown supplier";
    if ($[9] !== t8) {
      t6 = <div className="break-words text-body text-foreground">{t8}</div>;
      $[9] = t8;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t9;
    if ($[11] !== shipment.createdDate) {
      t9 = shipment.createdDate && <span>Received {formatDate(new Date(shipment.createdDate))}</span>;
      $[11] = shipment.createdDate;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== shipment.lotNumber) {
      t10 = shipment.lotNumber && <span>• Lot: {shipment.lotNumber}</span>;
      $[13] = shipment.lotNumber;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== shipment.expiryDate) {
      t11 = shipment.expiryDate && <span>• Exp: {formatDate(shipment.expiryDate)}</span>;
      $[15] = shipment.expiryDate;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9) {
      t7 = <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-caption text-foreground-muted">{t9}{t10}{t11}</div>;
      $[17] = t10;
      $[18] = t11;
      $[19] = t9;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const t12 = shipment.remainingQuantity > 0 ? "text-foreground-secondary" : "text-foreground-muted";
    if ($[21] !== t12) {
      t1 = cx("mt-1 text-caption", t12);
      $[21] = t12;
      $[22] = t1;
    } else {
      t1 = $[22];
    }
    if ($[23] !== shipment.remainingQuantity) {
      t2 = formatQuantity(shipment.remainingQuantity);
      $[23] = shipment.remainingQuantity;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    t3 = " ";
    t4 = formatUnit(unit);
    $[0] = getShipmentUnit;
    $[1] = shipment;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[25] !== t1 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t8 = <div className={t1}>{t2}{t3}{t4}{" "}available</div>;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}

// ============================================================================
// Quantity Controls Component
// ============================================================================

interface QuantityControlsProps {
  shipment: Shipment;
}
function QuantityControls(t0) {
  const $ = _c(51);
  const {
    shipment
  } = t0;
  const {
    getQuantity,
    setQuantity,
    canUseExtraYield,
    getCapacity,
    getShipmentUnit
  } = useShipmentPickerContext();
  let currentQty;
  let extraYield;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== canUseExtraYield || $[1] !== getQuantity || $[2] !== getShipmentUnit || $[3] !== shipment) {
    const unit = getShipmentUnit(shipment);
    let t5;
    if ($[10] !== canUseExtraYield || $[11] !== shipment) {
      t5 = canUseExtraYield(shipment);
      $[10] = canUseExtraYield;
      $[11] = shipment;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    extraYield = t5;
    currentQty = getQuantity(shipment.id);
    t4 = "flex flex-wrap items-center justify-between gap-2 py-2 pl-9 pr-2";
    t1 = "text-caption text-foreground-secondary";
    t2 = "Use (";
    t3 = formatUnit(unit);
    $[0] = canUseExtraYield;
    $[1] = getQuantity;
    $[2] = getShipmentUnit;
    $[3] = shipment;
    $[4] = currentQty;
    $[5] = extraYield;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    currentQty = $[4];
    extraYield = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t5 = <span className={t1}>{t2}{t3})</span>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = `Decrease quantity for lot ${shipment.lotNumber || shipment.id}`;
  let t7;
  if ($[17] !== currentQty || $[18] !== setQuantity || $[19] !== shipment.id) {
    t7 = e => {
      e.stopPropagation();
      setQuantity(shipment.id, currentQty - 1);
    };
    $[17] = currentQty;
    $[18] = setQuantity;
    $[19] = shipment.id;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = currentQty <= 0;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <RiSubtractLine className="h-3 w-3" />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t10 = <Button variant="secondary" type="button" size="icon" className="h-8 w-8" aria-label={t6} onClick={t7} disabled={t8}>{t9}</Button>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== setQuantity || $[27] !== shipment.id) {
    t11 = e_0 => setQuantity(shipment.id, parseFloat(e_0.target.value) || 0);
    $[26] = setQuantity;
    $[27] = shipment.id;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = `Quantity for lot ${shipment.lotNumber || shipment.id}`;
  const t13 = extraYield ? undefined : getCapacity(shipment);
  let t14;
  if ($[29] !== currentQty || $[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <Input type="number" value={currentQty} onChange={t11} onClick={_temp10} className="h-8 w-20 text-center tabular-nums" aria-label={t12} min={0} step="any" max={t13} />;
    $[29] = currentQty;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = `Increase quantity for lot ${shipment.lotNumber || shipment.id}`;
  let t16;
  if ($[34] !== currentQty || $[35] !== setQuantity || $[36] !== shipment.id) {
    t16 = e_2 => {
      e_2.stopPropagation();
      setQuantity(shipment.id, currentQty + 1);
    };
    $[34] = currentQty;
    $[35] = setQuantity;
    $[36] = shipment.id;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = !extraYield && currentQty >= getCapacity(shipment);
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <RiAddLine className="h-3 w-3" />;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t15 || $[40] !== t16 || $[41] !== t17) {
    t19 = <Button variant="secondary" type="button" size="icon" className="h-8 w-8" aria-label={t15} onClick={t16} disabled={t17}>{t18}</Button>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t10 || $[44] !== t14 || $[45] !== t19) {
    t20 = <div className="flex items-center gap-1">{t10}{t14}{t19}</div>;
    $[43] = t10;
    $[44] = t14;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20 || $[48] !== t4 || $[49] !== t5) {
    t21 = <div className={t4}>{t5}{t20}</div>;
    $[47] = t20;
    $[48] = t4;
    $[49] = t5;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}
function _temp10(e_1) {
  return e_1.stopPropagation();
}
function DepletedVisibilityControl() {
  const $ = _c(3);
  const {
    enableExtraYield,
    showDepleted,
    setShowDepleted
  } = useShipmentPickerContext();
  if (!enableExtraYield) {
    return null;
  }
  let t0;
  if ($[0] !== setShowDepleted || $[1] !== showDepleted) {
    t0 = <LotSelectionDepletedToggle checked={showDepleted} onChange={setShowDepleted} />;
    $[0] = setShowDepleted;
    $[1] = showDepleted;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function ExtraYieldWarning(t0) {
  const $ = _c(22);
  const {
    shipment
  } = t0;
  const {
    allocations,
    getShipmentUnit,
    getCapacity
  } = useShipmentPickerContext();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== allocations || $[1] !== getCapacity || $[2] !== getShipmentUnit || $[3] !== shipment) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const unit = getShipmentUnit(shipment);
      const allocation = allocations[shipment.id];
      if (!allocation?.allowExtraYield) {
        t6 = null;
        break bb0;
      }
      if ($[10] !== allocation.quantity || $[11] !== allocation.shipment || $[12] !== getCapacity) {
        const extra = roundQuantity(allocation.quantity - getCapacity(allocation.shipment));
        if (extra <= 0) {
          t6 = null;
          break bb0;
        }
        t1 = "status";
        t2 = "ml-9 mr-2 py-2 text-caption leading-relaxed text-status-warning-text";
        t3 = formatQuantity(extra);
        $[10] = allocation.quantity;
        $[11] = allocation.shipment;
        $[12] = getCapacity;
        $[13] = t1;
        $[14] = t2;
        $[15] = t3;
      } else {
        t1 = $[13];
        t2 = $[14];
        t3 = $[15];
      }
      t4 = " ";
      t5 = formatUnit(unit);
    }
    $[0] = allocations;
    $[1] = getCapacity;
    $[2] = getShipmentUnit;
    $[3] = shipment;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t7 = <p role={t1} className={t2}>{t3}{t4}{t5} extra yield will be recorded from this lot when saved.</p>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

// ============================================================================
// Summary Component
// ============================================================================

function Summary() {
  const $ = _c(18);
  const {
    totalAllocated,
    selectedCount,
    coverageUnit,
    quantityNeeded
  } = useShipmentPickerContext();
  if (totalAllocated === 0 && quantityNeeded === undefined) {
    return null;
  }
  let t0;
  if ($[0] !== selectedCount) {
    t0 = selectedCount !== 1 ? <span>{"s"}</span> : <span>{""}</span>;
    $[0] = selectedCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== selectedCount || $[3] !== t0) {
    t1 = <span className="text-body text-foreground-muted">Total from {selectedCount} lot{t0}</span>;
    $[2] = selectedCount;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== totalAllocated) {
    t2 = formatQuantity(totalAllocated);
    $[5] = totalAllocated;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== quantityNeeded) {
    t3 = quantityNeeded !== undefined && <span>{` of ${formatQuantity(quantityNeeded)}`}</span>;
    $[7] = quantityNeeded;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== coverageUnit) {
    t4 = <span>{coverageUnit}</span>;
    $[9] = coverageUnit;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <span className="text-body tabular-nums text-foreground">{t2}{t3}{" "}{t4}</span>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t5) {
    t6 = <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">{t1}{t5}</div>;
    $[15] = t1;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}

// ============================================================================
// Helper Functions
// ============================================================================

function buildInitialAllocations(selectedAllocations: ShipmentAllocation[]): Record<string, PickerAllocation> {
  const initial: Record<string, PickerAllocation> = {};
  selectedAllocations.forEach(alloc => {
    initial[alloc.shipmentId] = {
      quantity: alloc.quantity,
      shipment: alloc.shipment,
      ...(alloc.allowExtraYield && {
        allowExtraYield: true,
        decisionKey: alloc.decisionKey
      })
    };
  });
  return initial;
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const ShipmentPicker = {
  Root,
  DialogWrapper,
  Search,
  List,
  Item,
  SimpleItem,
  ItemDetails,
  QuantityControls,
  DepletedVisibilityControl,
  ExtraYieldWarning,
  Summary
};

// Also export the context hook for advanced use cases
export { useShipmentPickerContext };